import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface FormData {
    name: string;
    email: string;
    phone: string;
}

export interface FormErrors {
    name: string;
    email: string;
    phone: string;
}

export interface FormConfig {
    sheetId: string;
    interesse: string;
    observacoes?: string;
    sendEmail?: boolean;
    emailTemplateId?: string;
    emailInfo?: Record<string, any>;
    redirectPath?: string;
    whatsappUrl?: string;
    secretKey?: string;
}

export const useFormHandler = (config: FormConfig) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: ''
    });
    const [errors, setErrors] = useState<FormErrors>({
        name: '',
        email: '',
        phone: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Validation functions
    const validateName = (name: string): string => {
        if (!name.trim()) return 'Nome é obrigatório';
        if (name.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';
        return '';
    };

    const validateEmail = (email: string): string => {
        if (!email.trim()) return 'Email é obrigatório';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return 'Email inválido';
        return '';
    };

    const validatePhone = (phone: string): string => {
        if (!phone.trim()) return 'WhatsApp é obrigatório';
        const numericValue = phone.replace(/\D/g, '');
        if (numericValue.length < 10 || numericValue.length > 11) return 'Número de WhatsApp inválido';
        return '';
    };

    const formatPhoneNumber = (value: string): string => {
        // Remove all non-numeric characters
        const numericValue = value.replace(/\D/g, '');

        // Apply mask based on length
        if (numericValue.length <= 2) {
            return `(${numericValue}`;
        } else if (numericValue.length <= 7) {
            return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2)}`;
        } else if (numericValue.length <= 10) {
            return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2, 6)}-${numericValue.slice(6)}`;
        } else {
            return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2, 7)}-${numericValue.slice(7, 11)}`;
        }
    };

    // Generate HMAC-SHA256 code for email authentication
    const generateCode = async (timestamp: number): Promise<string> => {
        if (!config.secretKey) return '';
        
        const encoder = new TextEncoder();
        const keyData = encoder.encode(config.secretKey);
        const timestampData = encoder.encode(timestamp.toString());

        const cryptoKey = await crypto.subtle.importKey(
            'raw',
            keyData,
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign']
        );

        const signature = await crypto.subtle.sign('HMAC', cryptoKey, timestampData);
        const hashArray = Array.from(new Uint8Array(signature));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        return hashHex;
    };

    // Send email function
    const sendEmail = async (email: string, templateId: string, info: any): Promise<number | null> => {
        if (!config.secretKey) return null;

        try {
            const currentTimestamp = Math.floor(Date.now() / 1000);
            const code_authorization = await generateCode(currentTimestamp);

            const payload = {
                template_id: templateId,
                email: email,
                subject: "Sua Inscrição foi Confirmada",
                info: info,
                validation: {
                    code_authorization: code_authorization,
                    code_timestamp: currentTimestamp.toString()
                }
            };

            const response = await fetch('https://teraquiz.com/microservice.send.email/send.email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            return response.status;
        } catch (e) {
            console.error('Error sending email:', e);
            return null;
        }
    };

    const handleInputChange = (field: keyof FormData, value: string) => {
        if (field === 'phone') {
            const formattedValue = formatPhoneNumber(value);
            setFormData(prev => ({ ...prev, [field]: formattedValue }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        const nameError = validateName(formData.name);
        const emailError = validateEmail(formData.email);
        const phoneError = validatePhone(formData.phone);

        if (nameError || emailError || phoneError) {
            setErrors({
                name: nameError,
                email: emailError,
                phone: phoneError
            });
            setIsSubmitting(false);
            return;
        }

        // If validation passes, send the data to backend
        try {
            const response = await fetch('https://teraquiz.com/leads/send-lead', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sheet_id: config.sheetId,
                    data: {
                        nome: formData.name,
                        email: formData.email,
                        telefone: formData.phone,
                        interesse: config.interesse,
                        observacoes: config.observacoes || "Lead from landing page"
                    }
                })
            });

            if (response.ok) {
                console.log('Lead sent successfully');
                
                // Send email after successful lead submission (if configured and enabled)
                if (config.sendEmail && config.emailTemplateId && config.emailInfo) {
                    const emailStatus = await sendEmail(
                        formData.email,
                        config.emailTemplateId,
                        config.emailInfo
                    );

                    if (emailStatus && emailStatus >= 200 && emailStatus < 300) {
                        console.log('Email sent successfully');
                    } else {
                        console.error('Failed to send email');
                    }
                }

                // Redirect to WhatsApp or success page
                if (config.whatsappUrl) {
                    window.open(config.whatsappUrl, '_blank');
                    // Reset form after redirect
                    setFormData({ name: '', email: '', phone: '' });
                } else if (config.redirectPath) {
                    navigate(config.redirectPath);
                } else {
                    // Reset form
                    setFormData({ name: '', email: '', phone: '' });
                    alert('Inscrição realizada com sucesso!');
                }
            } else {
                console.error('Failed to send lead');
                alert('Erro ao enviar inscrição. Tente novamente.');
            }
        } catch (error) {
            console.error('Error sending lead:', error);
            alert('Erro ao enviar inscrição. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        formData,
        errors,
        isSubmitting,
        handleInputChange,
        handleSubmit
    };
};
